import * as bowser from "bowser";
import { formatISO } from "date-fns";
import _ from "lodash";
import { toJS } from "mobx";
import { Observer } from "mobx-react";
import React, { useContext } from "react";
import { BackendResponse } from "../api/BackendResponse";
import {
  CODE_NOT_LOGGED_IN_NEXT,
  CODE_NOT_LOGGED_IN_VISMA,
} from "../api/CUSTOM_STATUS_CODES";
import {
  isLoginCardShowing,
  isLoginModalShowing,
} from "../App/loginComponentsHelpers";
import { cvp_frontend_version } from "../cvp_frontend_version";
import { incrementValueVersion } from "./incrementValueVersion";
import { UserInfo } from "./UserInfo";
import { UserInfoContext } from "./UserInfoContext";
import { wsSentryReport } from "./wsSentryReport";

export const SENTRY_ERROR_WAS_HANDLED = "_SENTRY_ERROR_WAS_HANDLED_";

export type IWSSentryApiFunction = (args: {
  logitems: IWsSentryLogRow[];
}) => Promise<BackendResponse>;
export type IWSSentryInfoFunction = () => Promise<{
  userInfo: UserInfo;
}>;

let apiFunction: IWSSentryApiFunction = null;
export let infoFunction: IWSSentryInfoFunction = null;

export interface IWsSentryReportArgs {
  error?: any;
  tag?: string;
  showDialog?: boolean;
  dialogTitle?: string;
  simpleMessage: string;
  userInfo: UserInfo;
  eventId?: string;
  alreadyReported?: boolean;
  additionalErrorMessageInfo?: any[];
  [x: string]: any;
}

let _isInitialized = false;
export const wsSentryInit = (args: {
  apiFunction: IWSSentryApiFunction;
  infoFunction?: IWSSentryInfoFunction;
  userInfo: UserInfo;
}) => {
  if (_isInitialized) return;

  apiFunction = args.apiFunction;
  infoFunction = args.infoFunction;

  window.addEventListener("error", (error: any) => {
    error = error || {};
    wsSentryReport({
      userInfo: args.userInfo,
      tag: "window.onerror",
      simpleMessage: error.message,
      error: {
        message: error.message,
        filename: error.filename,
        lineno: error.lineno,
        colno: error.colno,
        error: error.error,
      },
    });
  });

  // promise polyfill should trigger this regardless of browser
  window.addEventListener("unhandledrejection", function (error: any) {
    error = error || {};
    wsSentryReport({
      tag: "window.unhandledrejection",
      userInfo: args.userInfo,
      simpleMessage: error.message,
      error: {
        message: error.message,
        filename: error.filename,
        lineno: error.lineno,
        colno: error.colno,
        error: error.error,
      },
    });
  });

  _isInitialized = true;
};

export interface IWsSentryLogRow {
  id?: number;
  id_konsern?: number;
  id_firma?: number;
  id_avdelinger?: number;
  id_ansatte?: number;
  wtcomponentsver?: string;
  serviceworkerver?: string;
  cvp_frontend_version?: string;
  asmversion?: string;
  browser?: string;
  osname?: string;
  useragentstring?: string;
  url?: string;
  userip?: string;
  jserror?: string;
  extra?: string;
  errorid?: string;
  tag?: string;
  alt1?: string;
  alt2?: string;
  alt3?: string;
  added?: string;
  browser_history_state?: string;
}

const sendAccumulatedReports = () => {
  let toSend = accumulatedReports.slice(0, 10);
  accumulatedReports = [];

  if (apiFunction) {
    const browser = bowser.getParser(window.navigator.userAgent);

    const logitems = toSend.map((t) => {
      const { eventId, tag, error, asm_version, userip, ...rest } = t;

      const stringifyData = (data) => {
        return safeStringify(data, (key, value) => toJS(value), 2);
      };

      return {
        id_konsern: (t.userInfo || {}).idConcern || -1,
        idFirmCandidate: (t.userInfo || {}).idFirmCandidate || -1,
        cvp_frontend_version,
        errorid: eventId || "",
        tag: tag || "",
        added: formatISO(new Date()),
        extra: stringifyData(rest) || "",
        jserror: stringifyData(error) || "",
        browser: browser.getBrowserName() + " " + browser.getBrowserVersion(),
        osname: browser.getOSName() + " " + browser.getOSVersion(),
        useragentstring: navigator.userAgent,
        url: window.location.href,
        browser_history_state: stringifyData(window.history.state) || "",
      } as IWsSentryLogRow;
    });

    apiFunction({ logitems });
  } else {
    console.warn("WsSentry apiFunction is not set");
  }
};

let accumulatedReports: IWsSentryReportArgs[] = [];
let timeOfPreviousReports = [];
let timeouts = [];

const getTimeoutLength = () => {
  const nReportsLastMinute = timeOfPreviousReports.length;

  if (nReportsLastMinute < 4) {
    return 0;
  }
  if (nReportsLastMinute < 10) {
    return 1000;
  }
  if (nReportsLastMinute < 20) {
    return 2000;
  }
  return 1000 * 20;
};

export const accumulateReport = (args: IWsSentryReportArgs) => {
  // maintain timeOfPreviousReports
  const now = new Date().getTime();
  timeOfPreviousReports = [...timeOfPreviousReports, now].filter(
    (d) => now - d < 1000 * 60
  );

  accumulatedReports.push(args);

  timeouts.forEach((t) => clearTimeout(t));
  timeouts = [];
  timeouts.push(setTimeout(sendAccumulatedReports, getTimeoutLength()));
};

// copy of https://github.com/debitoor/safe-json-stringify/blob/master/index.js
// with escape for react components
const safeStringify = (() => {
  const hasProp = Object.prototype.hasOwnProperty;

  function throwsMessage(err) {
    return "[Throws: " + (err ? err.message : "?") + "]";
  }

  function safeGetValueFromPropertyOnObject(obj, property) {
    if (hasProp.call(obj, property)) {
      try {
        return obj[property];
      } catch (err) {
        return throwsMessage(err);
      }
    }

    return obj[property];
  }

  function ensureProperties(obj) {
    const seen = []; // store references to objects we have seen before

    function visit(obj) {
      if (obj && obj.$$typeof) {
        return "[React]";
      }

      if (obj === null || typeof obj !== "object") {
        return obj;
      }

      if (seen.indexOf(obj) !== -1) {
        return "[Circular]";
      }
      seen.push(obj);

      if (typeof obj.toJSON === "function") {
        try {
          const fResult = visit(obj.toJSON());
          seen.pop();
          return fResult;
        } catch (err) {
          return throwsMessage(err);
        }
      }

      if (Array.isArray(obj)) {
        const aResult = obj.map(visit);
        seen.pop();
        return aResult;
      }

      const result = Object.keys(obj).reduce(function (result, prop) {
        // prevent faulty defined getter properties
        result[prop] = visit(safeGetValueFromPropertyOnObject(obj, prop));
        return result;
      }, {});
      seen.pop();
      return result;
    }

    return visit(obj);
  }

  return function (data, replacer?, space?) {
    if (data instanceof Error) {
      const stringified = JSON.stringify(
        data,
        Object.getOwnPropertyNames(data),
        space
      );

      if (stringified) return stringified;
    }

    return JSON.stringify(ensureProperties(data), replacer, space);
  };
})();

export interface SentryAssertDataOptions {
  userInfo: UserInfo;
  mapSentryData?: (data: BackendResponse) => BackendResponse;
}

export const sentryAssertData = (
  data: BackendResponse | BackendResponse[],
  options: SentryAssertDataOptions
) => {
  let datas: BackendResponse[] = [];
  if (!Array.isArray(data)) datas = [data];
  else datas = data;

  const { userInfo, mapSentryData: _mapSentryData } = options;

  const mapSentryData = (data: BackendResponse) => {
    if (_mapSentryData) {
      try {
        return _mapSentryData(data);
      } catch (e) {}
    }

    return data;
  };

  datas = datas.map(mapSentryData);

  datas.forEach((data) => {
    if (data && typeof data === "object") {
      assertedData[data.__dataId] = true;
    }
  });

  const withError = datas.filter((data) => data && !data.ok);

  if (withError.length > 0) {
    dataErrorsQueuedForDialog.push(
      ...withError.map((data) => ({ data, userInfo }))
    );
    showSentryReportSoon();
  }
};

interface DataError {
  data: BackendResponse;
  userInfo: UserInfo;
}
let dataErrorsQueuedForDialog: DataError[] = [];

const showSentryReportSoon = _.debounce(() => {
  let reportNow = [...dataErrorsQueuedForDialog];
  dataErrorsQueuedForDialog = [];

  const _isLoginComponentsShowing =
    isLoginModalShowing() || isLoginCardShowing();

  reportNow = reportNow.filter((d) => {
    const isNotLoggedInError = [
      CODE_NOT_LOGGED_IN_NEXT,
      CODE_NOT_LOGGED_IN_VISMA,
    ].includes(d.data.statusCode);

    if (!isNotLoggedInError) {
      return true;
    }

    if (_isLoginComponentsShowing) return false;

    if (
      typeof d.data.valueVersion === "number" &&
      d.data.valueVersion < hideNotLoggedInErrorsWithValueVersionSmallerThan
    ) {
      return false;
    }

    return true;
  });

  if (reportNow.length > 0) {
    const messages = _.uniq(reportNow.map((d) => d.data.message));

    wsSentryReport({
      simpleMessage: messages.join("\n"),
      error: messages.join("\n"),
      data: reportNow,
      userInfo: reportNow[0].userInfo,
      showDialog: true,
    });
  }
}, 1000);

let hideNotLoggedInErrorsWithValueVersionSmallerThan = 0;
export const clearNotLoggedInErrors = () => {
  hideNotLoggedInErrorsWithValueVersionSmallerThan = incrementValueVersion();
};

export const assertedData: { [dataId: string]: boolean } = {};

export const sentryAssert = (
  check: boolean,
  error: string | null | undefined
) => {
  if (!check) {
    throw new Error(error);
  }
};

export interface SentryAssertProps {
  assert?: () => string | null | undefined;
  assertData?: () => BackendResponse[];
}

export const SentryAssert = ({ assert, assertData }: SentryAssertProps) => {
  const userInfo = useContext(UserInfoContext);

  return (
    <Observer
      render={() => {
        if (assertData) {
          assertData().forEach((data) => {
            sentryAssertData(data, { userInfo });
          });
        }

        if (assert) {
          const err = assert();
          if (err) throw new Error(err);
        }

        return null;
      }}
    />
  );
};
